import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, filter, take, takeUntil } from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FusePerfectScrollbarDirective } from '@fuse/directives/fuse-perfect-scrollbar/fuse-perfect-scrollbar.directive';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { Users } from 'app/main/services/users';
import { AuthService } from 'app/main/services/auth.service';
import { NavCounterService } from 'app/main/services/nav-counter.service';
import { MenusService } from 'app/main/services/menus.service';
import { TranslateService } from '@ngx-translate/core';
import { version } from '../../../../../../../package.json';
import { HelpersService } from 'app/main/services/helpers.service';

@Component({
    selector     : 'navbar-vertical-style-1',
    templateUrl  : './style-1.component.html',
    styleUrls    : ['./style-1.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NavbarVerticalStyle1Component implements OnInit, OnDestroy
{
    fuseConfig: any;
    navigation: any;
    currentUser: Users;
    // Private
    private _fusePerfectScrollbar: FusePerfectScrollbarDirective;
    private _unsubscribeAll: Subject<any>;
    public version: string = version;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {Router} _router
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _router: Router,
        private auth: AuthService,
        private counter: NavCounterService,
        private menuService: MenusService,
        private _translateService: TranslateService,
        public helper: HelpersService
    )
    {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
        this.currentUser = this.auth.getLoggedUser();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    // Directive
    @ViewChild(FusePerfectScrollbarDirective, {static: true})
    set directive(theDirective: FusePerfectScrollbarDirective)
    {
        if ( !theDirective )
        {
            return;
        }

        this._fusePerfectScrollbar = theDirective;

        // Update the scrollbar on collapsable item toggle
        this._fuseNavigationService.onItemCollapseToggled
            .pipe(
                delay(500),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._fusePerfectScrollbar.update();
            });

        // Scroll to the active item position
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => {
                    setTimeout(() => {
                        this._fusePerfectScrollbar.scrollToElement('navbar .nav-link.active', -120);
                    });
                }
            );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                    if ( this._fuseSidebarService.getSidebar('navbar') )
                    {
                        this._fuseSidebarService.getSidebar('navbar').close();
                    }
                }
            );


        // Get current navigation
        this._fuseNavigationService.onNavigationChanged
            .pipe(
                filter(value => value !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.navigation = this._fuseNavigationService.getCurrentNavigation();
            });

        // Get Menu
        this.menuService.mainMenu$.subscribe(() => {
          this.menuService.main().subscribe(({data}) => {
            this.navigation = data;
            if (this._fuseNavigationService.isNavigationKeyExist('pmo')) {
              this._fuseNavigationService.unregister('pmo');
            }
            this._fuseNavigationService.register('pmo', this.navigation);
            this._fuseNavigationService.setCurrentNavigation('pmo');
            setTimeout(() => {
              this._translateService.setDefaultLang('en');
              this._translateService.setDefaultLang(this._translateService.currentLang);
            });
            if (this.currentUser && this.currentUser.account_accesses && this.currentUser.account_accesses.length) {
              if (this.auth.isProjectUser(this.currentUser.account_accesses) || this.auth.isSuperadmin(this.currentUser.account_accesses)) {
                this.counter.projectRefresh$.next(true);
                this.counter.invoicesRefresh$.next(true);
              }
              if (this.auth.isLogisticUser(this.currentUser.account_accesses) || this.auth.isSuperadmin(this.currentUser.account_accesses)) {
                this.counter.approvalRefresh$.next(true);
              }
              if (this.auth.isSuperadmin(this.currentUser.account_accesses)){
                this.counter.regionsRefresh$.next(true);
                this.counter.userRefresh$.next(true);
              }
            }
          });
        });

        // Subscribe to the config changes
        this._fuseConfigService.config
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((config) => {
            this.fuseConfig = config;
        });
        this.counterSubscribe();

    }

    counterSubscribe(){
      // Subscribe project counter
      this.counter.projectRefresh$.subscribe(() => {
        this.counter.countProjecs().subscribe(res => {
          const { all } = res;
          this.updateBadge('project-progress', all);
          // console.log(res)
          Object.keys(res).forEach(k => this.updateBadge(`project-${k}`, res[k]));
        });
      });
      // Subscribe region counter
      this.counter.regionsRefresh$.subscribe(() => {
        this.counter.countRegions().subscribe(res => {
          Object.keys(res).forEach(k => this.updateBadge(`regions-${k}`, res[k]));
        });
      });
      // Subscribe users counter
      this.counter.userRefresh$.subscribe(() =>  {
        this.counter.countUsers().subscribe(res => {
          Object.keys(res).forEach(k => this.updateBadge(`users-${k}`, res[k]));
        });
      });
      // Subscribe invoice draft counter
      this.counter.invoicesRefresh$.subscribe(() => {
        this.counter.invoices('draft').pipe(takeUntil(this._unsubscribeAll)).subscribe( resp => {
          if (resp.data.count) {
            this.updateBadge('invoice-draft', resp.data.count);
          }
        });
      });
      // Subscribe approval counter
      this.counter.approvalRefresh$.subscribe(() =>  {
        this.counter.countApproval().subscribe(res => {
          Object.keys(res).forEach(k => this.updateBadge(`approval-${k}`, res[k]));
        });
      });
    }

    private updateBadge(id, count): void{
      const title = count.toString();
      this._fuseNavigationService.updateNavigationItem(id, {
        badge: {
          title,
        }
      });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar opened status
     */
    toggleSidebarOpened(): void
    {
        this._fuseSidebarService.getSidebar('navbar').toggleOpen();
    }

    /**
     * Toggle sidebar folded status
     */
    toggleSidebarFolded(): void
    {
        this._fuseSidebarService.getSidebar('navbar').toggleFold();
    }
}
